import React, { useEffect, useState } from "react";
import BootstrapModal from "react-bootstrap/Modal";
import { RxCross2 } from "react-icons/rx";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "../../../../Shared/Icons";
import { styles } from "../../../../Utils/Common";

interface RejectModalProps {
  show: boolean;
  onHide: () => void;
  requisitionOrderId: number;
  requisitionId?: number;
  onSuccess?: () => void;
}

const RejectModal: React.FC<RejectModalProps> = ({
  show,
  onHide,
  requisitionOrderId,
  requisitionId,
  onSuccess,
}) => {
  const { t } = useLang();
  const [rejectionReason, setRejectionReason] = useState<any[]>([]);
  const [selectedReason, setSelectedReason] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [inputFields, setInputFields] = useState<boolean>(false);
  const [buttonDisable, setButtonDisable] = useState<boolean>(false);
  const [showReason, setShowReason] = useState<any>(null);
  const [check, setCheck] = useState(false);
  const [error, setError] = useState<string>("");
  const [addRejection, setAddRejection] = useState<{
    subject: string;
    description: string;
  }>({
    subject: "",
    description: "",
  });

  useEffect(() => {
    if (show) {
      getRejectionReasonLookup();
      setSelectedReason(null);
      setReason("");
      setShowReason(null);
      setError("");
      setInputFields(false);
      setAddRejection({ subject: "", description: "" });
    }
  }, [show]);

  const getRejectionReasonLookup = async () => {
    try {
      const res: any = await RequisitionType.GetRejectionReasonLookup();
      if (res?.data) {
        setRejectionReason(res.data);
      }
    } catch (error) {
      console.error("Error fetching rejection reason lookup:", error);
      toast.error(t("Error loading rejection reasons"));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddRejection((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddReasonClick = () => {
    setInputFields(true);
  };

  const handleAddReasonClickClose = () => {
    setInputFields(false);
    setAddRejection({
      subject: "",
      description: "",
    });
  };

  const addRejectionReason = async () => {
    if (!addRejection.subject || !addRejection.description) {
      toast.error(t("Please fill all the required fields."));
      return;
    }

    setButtonDisable(true);
    const obj = {
      text: addRejection.subject,
      subject: addRejection.subject,
      description: addRejection.description,
      requisitionAssignment: [],
    };

    try {
      const res: any = await RequisitionType.AddRejectionReason(obj);
      if (res?.status === 200) {
        toast.success(t(res?.data?.message || "Reason added successfully"));
        setButtonDisable(false);
        handleAddReasonClickClose();
        await getRejectionReasonLookup();
        setAddRejection({ subject: "", description: "" });
      } else {
        toast.error(t(res?.data?.message || "Failed to add reason"));
        setButtonDisable(false);
      }
    } catch (error) {
      console.error("Error adding rejection reason:", error);
      toast.error(t("Error adding reason"));
      setButtonDisable(false);
    }
  };

  const handleDeleteReason = async (reasonId: number) => {
    try {
      const res = await RequisitionType.RequisitionRejectionDelete(reasonId);
      if (res?.data?.httpStatusCode === 200) {
        await getRejectionReasonLookup();
        toast.success(res?.data?.message || t("Reason deleted successfully"));
      } else {
        toast.error(res?.data?.message || t("Failed to delete reason"));
      }
    } catch (error) {
      console.error("Error deleting reason:", error);
      toast.error(t("Error deleting reason"));
    }
  };

  const handleChangeForActionReason = (message: string) => {
    setReason(message);
    setError("");
  };

  const handleChangeRejectionReasonDropdown = async (selectedOption: any) => {
    setSelectedReason(selectedOption?.value);
    setError("");
    
    const obj = {
      item1: selectedOption?.label,
    };
    try {
      const res: any = await RequisitionType.ShowRejectionReason(obj);
      if (res?.data) {
        setShowReason(res.data);
      }
    } catch (error) {
      console.error("Error fetching rejection reason details:", error);
    }
  };

  const handleReject = async () => {
    if (buttonDisable) return;

    if (!selectedReason) {
      setError(t("Please Select Reason"));
      return;
    }

    if (!reason || !reason.trim()) {
      setError(t("Please Enter Rejection Comments"));
      return;
    }

    setButtonDisable(true);
    setCheck(true);

    const obj = {
      statusId: 4,
      RequisitionOrderIds: [requisitionOrderId],
      ActionReasons: showReason?.subject || reason,
    };

    try {
      const res: any = await RequisitionType.ViewRequisitionBulkStatusChange(obj);
      
      if (res?.status === 200) {
        // Save rejection reason
        if (showReason?.subject) {
          const saveObj = {
            assignments: [{
              requisitionId: requisitionId || 0,
              requisitionOrderId: requisitionOrderId,
              requisitionType: 0, // May need to get this from props if available
            }],
            rejection: showReason.subject,
          };
          await RequisitionType.SaveRequisitionAgainstReason(saveObj);
        }

        toast.success(t(res?.data?.message || "Order rejected successfully"));
        handleModalClose();
        onSuccess?.();
      } else {
        toast.error(t(res?.data?.message || "Failed to reject order"));
        setError(t("Failed to reject order"));
      }
    } catch (error) {
      console.error("Error rejecting order:", error);
      toast.error(t("An error occurred while processing the request."));
      setError(t("An error occurred while processing the request."));
    } finally {
      setButtonDisable(false);
      setCheck(false);
    }
  };

  const handleModalClose = () => {
    setSelectedReason(null);
    setReason("");
    setShowReason(null);
    setInputFields(false);
    setError("");
    setAddRejection({
      subject: "",
      description: "",
    });
    setButtonDisable(false);
    setCheck(false);
    onHide();
  };

  const Option = (props: any) => {
    const { data, innerRef, innerProps } = props;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 10,
          paddingLeft: 10,
          paddingTop: 5,
          paddingBottom: 5,
        }}
      >
        <span
          ref={innerRef}
          {...innerProps}
          style={{ marginLeft: 8, cursor: "pointer" }}
        >
          {data.label}
        </span>
        <RxCross2
          color="red"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteReason(data.value);
          }}
        />
      </div>
    );
  };

  return (
    <BootstrapModal
      show={show}
      onHide={handleModalClose}
      backdrop="static"
      keyboard={false}
    >
      <BootstrapModal.Header
        closeButton
        className="bg-light-warning m-0 p-5"
      >
        <h5>{t("Reason")}</h5>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        <div className="fv-row mb-4">
          <label htmlFor="status" className="mb-2 required form-label">
            {t("Select Reason")}
          </label>
          <Select
            options={rejectionReason}
            components={{ Option }}
            placeholder={t("Select Reason")}
            theme={(theme) => styles(theme)}
            isSearchable={true}
            onChange={handleChangeRejectionReasonDropdown}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: "var(--kt-input-border-color)",
                color: "var(--kt-input-border-color)",
              }),
            }}
            value={rejectionReason.find(
              (option: any) => option.value === selectedReason
            )}
            isDisabled={buttonDisable}
          />
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-warning btn-sm mb-2"
            onClick={handleAddReasonClick}
            disabled={buttonDisable}
          >
            {t("Add Another Reason")}
          </button>
        </div>
        {inputFields && (
          <>
            <div>
              <div className="mb-3">
                <label className="form-label required">{t("Subject")}</label>
                <input
                  type="text"
                  name="subject"
                  className="form-control bg-transparent"
                  placeholder={t("Subject")}
                  value={addRejection.subject}
                  onChange={handleInputChange}
                  disabled={buttonDisable}
                />
              </div>
              <div className="mb-3">
                <label className="form-label required">
                  {t("Description")}
                </label>
                <input
                  type="text"
                  name="description"
                  className="form-control bg-transparent"
                  placeholder={t("Description")}
                  value={addRejection.description}
                  onChange={handleInputChange}
                  disabled={buttonDisable}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-info btn-sm mb-2"
                onClick={handleAddReasonClickClose}
                disabled={buttonDisable}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn btn-primary btn-sm mb-2"
                onClick={addRejectionReason}
                disabled={buttonDisable || !addRejection.subject || !addRejection.description}
              >
                {buttonDisable ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t("Saving...")}
                  </>
                ) : (
                  t("Save")
                )}
              </button>
            </div>
          </>
        )}

        {showReason && (
          <div className="mt-3 mb-3">
            <span className="fw-bold">{t("Reason")} : </span>
            <span className="text-danger">
              {showReason?.subject}
            </span>
          </div>
        )}

        <div className="mt-5">
          <label className="form-label">{t("Rejection Comment")}</label>
          <textarea
            name="actionReasons"
            className="form-control bg-transparent mb-3 mb-lg-0 h-50px"
            placeholder={t("Rejection Comments")}
            required
            value={reason}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleChangeForActionReason(event?.target?.value)
            }
            disabled={buttonDisable}
          />
        </div>
        {error ? <span style={{ color: "red" }}>{error}</span> : null}
      </BootstrapModal.Body>
      <BootstrapModal.Footer className="p-0">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleModalClose}
          disabled={buttonDisable}
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm m-2"
          disabled={buttonDisable || !selectedReason || !reason.trim()}
          style={{
            pointerEvents: buttonDisable ? "none" : "auto",
            opacity: buttonDisable ? 0.6 : 1,
          }}
          onClick={handleReject}
        >
          <span>{check ? <LoaderIcon /> : null}</span>{" "}
          <span>{t("Save")}</span>
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default RejectModal;
